import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMG } from "@/lib/images";
import { Chapter, Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import SectionCTA from "@/components/SectionCTA";
import { EQUITABLE_EXAMPLES, usePageMeta } from "@/lib/site";

const PILLARS = [
  {
    n: "01",
    t: "Usable Space",
    d: "We look at how your property's square footage — existing and potential — can be put to better work: additions, garage apartments, converted areas, and expanded living space.",
  },
  {
    n: "02",
    t: "Functionality",
    d: "A project should improve how the property works day to day — flow, privacy, storage, parking, and spaces that serve the way you actually live or do business.",
  },
  {
    n: "03",
    t: "Long-Term Value",
    d: "We plan improvements with the future in mind, helping you make smarter decisions about where and how you invest in construction — without promising specific financial outcomes.",
  },
];

export default function EquitableConstruction() {
  usePageMeta(
    "Equitable Construction — Smarter Building in Utah",
    "Equitable Construction is Black Sky Builders' strategic approach to additions, garages, ADU-style spaces, and renovations — increasing usable space, functionality, and long-term property value."
  );

  return (
    <div data-testid="equitable-page">
      <PageHero
        eyebrow="The Black Sky Differentiator"
        lines={["Equitable", "Construction"]}
        copy="A strategic approach to construction and property improvement — focused on increasing usable space, improving functionality, and creating meaningful long-term value for your property."
        image={IMG.renovation}
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16" data-testid="equitable-manifesto">
        <Chapter
          index="01"
          eyebrow="The Philosophy"
          title="Look beyond the project"
          copy="Most contractors price what you point at. Equitable Construction starts earlier: we evaluate how additions, expansions, garages, additional living spaces, renovations, and other improvements can increase usable space, improve functionality, and contribute meaningful long-term value to the property."
        />
        <div className="grid gap-6 lg:grid-cols-2">
          <Reveal>
            <p className="text-sm leading-relaxed text-mist sm:text-base">
              The goal is simple: help property owners make smarter decisions about where and how they invest in
              construction. Sometimes that means building bigger. Often it means building differently — a garage
              with living space above it instead of a garage alone, an underutilized area converted into a
              mother-in-law suite, or a renovation sequenced around the improvements that matter most.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-sm leading-relaxed text-mist sm:text-base">
              Equitable Construction is a construction philosophy, not a financial product. We don't promise
              appraisals, equity amounts, or investment returns. We promise a disciplined way of thinking about
              your property — and a team that can plan, coordinate, and build whatever direction you choose.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-line bg-coal">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16" data-testid="equitable-pillars">
          <Chapter index="02" eyebrow="Three Pillars" title="What every project is measured against" />
          <div className="grid gap-px border border-line bg-line md:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col bg-coal p-8">
                  <span className="font-mono text-3xl font-bold tracking-[0.1em] text-steel">{p.n}</span>
                  <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-tight">{p.t}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-fog">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16" data-testid="equitable-examples">
        <Chapter
          index="03"
          eyebrow="What It Looks Like"
          title="Equitable Construction in practice"
          copy="Common projects where a strategic approach changes what a property can do for its owner."
        />
        <div className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
          {EQUITABLE_EXAMPLES.map((ex, i) => (
            <Reveal key={ex} delay={(i % 4) * 0.06} className="h-full">
              <div className="flex h-full items-start gap-4 bg-ink p-5">
                <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rotate-45 bg-steel" />
                <span className="text-sm leading-relaxed text-mist">{ex}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <p className="max-w-2xl text-sm leading-relaxed text-fog">
            Wondering what your property could do? Bring us the address and the idea — we'll help you evaluate
            the options.
          </p>
          <Link to="/contact" data-testid="equitable-cta-button" className="btn-primary mt-6">
            Request a Project Consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      <section className="border-t border-line">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8 lg:px-16">
          <p className="font-mono text-[11px] leading-relaxed tracking-wide text-steel" data-testid="equitable-disclaimer">
            Equitable Construction is a construction philosophy focused on making smarter use of your property and
            construction investment. Black Sky Builders does not guarantee property-value increases, appraisals,
            equity amounts, investment returns, or financial results.
          </p>
        </div>
      </section>

      <SectionCTA
        title="Make your property work harder"
        copy="Tell us what you're considering — we'll help you evaluate the smartest way to build it."
      />
    </div>
  );
}
